import {
  CalendarOutlined,
  LockOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { Alert, Form, Input, Popover, Select, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import { errorMessage } from '@/api/client';
import { useAuth } from '@/auth/AuthContext';
import { LocaleFlag } from '@/components/LocaleFlag';
import i18n, { SUPPORTED_LOCALES, localeDescriptor } from '@/i18n';

import './login.css';

/** Почта службы поддержки — сюда пишут, если забыли пароль. */
const SUPPORT_EMAIL = 'info@fleetcore.uz';

interface LoginForm {
  email: string;
  password: string;
}

export function LoginPage() {
  const { t } = useTranslation();
  const { user, login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/" replace />;

  // Выбор запоминается тем же ключом, что и в основном интерфейсе: язык,
  // выбранный на входе, должен остаться после него.
  const changeLocale = (locale: string): void => {
    localStorage.setItem('tms.locale', locale);
    void i18n.changeLanguage(locale);
  };

  const onFinish = async (values: LoginForm): Promise<void> => {
    setError(null);
    setSubmitting(true);
    try {
      // Офис не выбирается на форме входа: пользователь попадает
      // в свой офис по умолчанию, а переключается уже внутри системы.
      await login(values.email, values.password);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  // Самостоятельного сброса пароля в системе нет (почтовый сервис намеренно
  // не подключён), поэтому ссылка открывает не форму, а подсказку, куда
  // написать. Подсказка всплывает у самой ссылки, а не окном посреди
  // страницы: взгляд не уходит от того места, куда человек только что нажал.
  // Адрес — ссылка mailto: с готовой темой письма.
  const forgotPasswordHint = (
    <div className="tms-login-forgot-hint">
      <div>{t('Отправьте письмо на электронную почту')}</div>
      <Typography.Link
        href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(t('Восстановление пароля FleetCore'))}`}
        strong
        style={{ fontSize: 16 }}
      >
        {SUPPORT_EMAIL}
      </Typography.Link>
      <div className="tms-login-forgot-hint-note">
        {t('Укажите в письме свою почту для входа — администратор задаст новый пароль.')}
      </div>
    </div>
  );

  return (
    /*
      Две половины в одной скруглённой карточке: слева живое фото человека
      за работой с техникой, справа — тёмная форма входа. Самостоятельной
      регистрации нет, поэтому переключателя «Регистрация / Вход» тоже нет.
    */
    <div className="tms-login">
      <div className="tms-login-frame">
        <aside className="tms-login-photo">
          <div className="tms-login-brand">
            <span>Fleet</span>
            <b>Core</b>
          </div>

          {/* Плашка поверх фото — не данные системы, а сегодняшняя дата:
              на общем рабочем месте по ней видно, что экран живой. */}
          <div className="tms-login-chips">
            <LoginTodayChip />
          </div>
        </aside>

        <main className="tms-login-side">
          {/*
            Язык выбирается до входа намеренно: сотрудник, которому русский
            интерфейс незнаком, иначе не понял бы даже подписи полей формы.
          */}
          <Select
            className="tms-login-locale"
            size="large"
            classNames={{ popup: { root: 'tms-login-locale-popup' } }}
            value={localeDescriptor(i18n.language).code}
            popupMatchSelectWidth={false}
            optionLabelProp="label"
            onChange={changeLocale}
            options={SUPPORTED_LOCALES.map((locale) => ({
              value: locale.code,
              label: (
                <Space size={8}>
                  <LocaleFlag code={locale.flag} />
                  {locale.code.toUpperCase()}
                </Space>
              ),
            }))}
          />

          <div className="tms-login-form">
            <h1>{t('Добро пожаловать в FleetCore!')}</h1>
            <p className="tms-login-lead">{t('Войдите, чтобы продолжить работу')}</p>

            <Form<LoginForm> layout="vertical" onFinish={onFinish} requiredMark={false}>
              {error && (
                <Alert type="error" message={error} showIcon style={{ marginBottom: 18 }} />
              )}

              <Form.Item
                name="email"
                label={t('Электронная почта')}
                rules={[
                  { required: true, message: t('Введите электронную почту') },
                  { type: 'email', message: t('Неверный формат электронной почты') },
                ]}
              >
                <Input
                  size="large"
                  prefix={<MailOutlined />}
                  autoComplete="username"
                  autoFocus
                  placeholder={t('Введите электронную почту')}
                />
              </Form.Item>

              <Form.Item
                name="password"
                label={t('Пароль')}
                rules={[
                  { required: true, message: t('Введите пароль') },
                  { min: 8, message: t('Пароль — не короче 8 символов') },
                ]}
                style={{ marginBottom: 12 }}
              >
                <Input.Password
                  size="large"
                  prefix={<LockOutlined />}
                  autoComplete="current-password"
                  placeholder={t('Введите пароль')}
                />
              </Form.Item>

              <div className="tms-login-forgot">
                <Popover
                  trigger="click"
                  placement="bottomLeft"
                  title={t('Забыли пароль?')}
                  content={forgotPasswordHint}
                >
                  <Typography.Link>{t('Забыли пароль?')}</Typography.Link>
                </Popover>
              </div>

              <button type="submit" className="tms-login-submit" disabled={submitting}>
                {submitting ? t('Вход…') : t('Войти')}
              </button>
            </Form>

            <p className="tms-login-note">
              {t('Нет доступа?')} {t('Обратитесь к администратору вашего офиса')}
              {' — '}
              {t('учётные записи заводятся вручную, самостоятельной регистрации в системе нет.')}
            </p>
          </div>
        </main>
      </div>

      {/* Строка под карточкой — подпись продукта, как у деловых систем:
          слева авторское право и назначение, справа контакт и версия
          (версия нужна поддержке: «на какой версии вы работаете»). */}
      <footer className="tms-login-footer">
        <span>
          © {copyrightYears()} FleetCore · {t('Платформа управления автопарком')}
        </span>
        <span>
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> · v{__APP_VERSION__}
        </span>
      </footer>
    </div>
  );
}

/** Год, с которого ведётся проект; в подписи — диапазон до текущего года («2024–2026»). */
const LAUNCH_YEAR = 2024;

function copyrightYears(): string {
  const year = new Date().getFullYear();
  return year > LAUNCH_YEAR ? `${LAUNCH_YEAR}–${year}` : String(LAUNCH_YEAR);
}

function LoginChip({ icon, title, note }: { icon: ReactNode; title: string; note: string }) {
  return (
    <div className="tms-login-chip">
      <span className="tms-login-chip-icon">{icon}</span>
      <span>
        <b>{title}</b>
        <small>{note}</small>
      </span>
    </div>
  );
}

/** Сегодняшняя дата и время — по ним на общем рабочем месте видно, что экран живой. */
function LoginTodayChip() {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => dayjs());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(dayjs()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  // С заглавной только день недели: CSS-правило capitalize подняло бы и месяц.
  const date = now.format('dddd, D MMMM');

  return (
    <LoginChip
      icon={<CalendarOutlined />}
      title={`${t('Сегодня')} · ${now.format('HH:mm')}`}
      note={date.charAt(0).toUpperCase() + date.slice(1)}
    />
  );
}
